import { Link } from 'react-router-dom';
import {
  Activity,
  ArrowRight,
  Award,
  CheckCircle2,
  Flame,
  Loader2,
  Target,
  TrendingDown,
  XCircle,
} from 'lucide-react';
import type { Dashboard as DashboardData } from '@pglearn/shared';
import { Card, CardHeader } from '../components/ui/Card.js';
import { cn } from '../lib/cn.js';
import { useDashboard } from '../lib/api-hooks.js';

function Stat({
  label,
  value,
  sub,
  icon: Icon,
}: {
  label: string;
  value: string;
  sub?: string;
  icon?: typeof Flame;
}) {
  return (
    <div>
      <p className="flex items-center gap-1.5 text-[11px] font-medium tracking-wide text-slate-500 uppercase dark:text-slate-400">
        {Icon && <Icon className="size-3.5" aria-hidden="true" />}
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold text-slate-900 tabular-nums dark:text-slate-100">
        {value}
      </p>
      {sub && <p className="text-xs text-slate-500 dark:text-slate-400">{sub}</p>}
    </div>
  );
}

/** A 30-day activity strip. Denser and more honest than a single streak number. */
function ActivityStrip({ days }: { days: DashboardData['activityDays'] }) {
  const byDate = new Map(days.map((day) => [day.date, day]));
  const cells: { date: string; solved: number }[] = [];

  for (let offset = 29; offset >= 0; offset -= 1) {
    const date = new Date(Date.now() - offset * 86_400_000).toISOString().slice(0, 10);
    cells.push({ date, solved: byDate.get(date)?.exercisesSolved ?? 0 });
  }

  return (
    <div className="flex flex-wrap gap-1" role="img" aria-label="Activity over the last 30 days">
      {cells.map((cell) => (
        <span
          key={cell.date}
          title={`${cell.date}: ${cell.solved} solved`}
          className={cn(
            'size-3 rounded-[3px]',
            cell.solved === 0 && 'bg-slate-200 dark:bg-slate-800',
            cell.solved >= 1 && cell.solved < 3 && 'bg-brand-300 dark:bg-brand-900',
            cell.solved >= 3 && cell.solved < 6 && 'bg-brand-500 dark:bg-brand-700',
            cell.solved >= 6 && 'bg-brand-700 dark:bg-brand-500',
          )}
        />
      ))}
    </div>
  );
}

export function Dashboard() {
  const dashboard = useDashboard();

  if (dashboard.isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" role="status">
        <Loader2 className="size-6 animate-spin text-slate-400" aria-hidden="true" />
        <span className="sr-only">Loading your dashboard</span>
      </div>
    );
  }

  if (dashboard.isError || !dashboard.data) {
    return (
      <p className="mx-auto max-w-3xl px-6 py-12 text-sm text-slate-500 dark:text-slate-400">
        Your dashboard could not be loaded.
      </p>
    );
  }

  const data = dashboard.data;
  const earnedBadges = data.badges.filter((badge) => badge.earned);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
        Welcome back, {data.displayName.split(' ')[0]}
      </h1>

      {/* --- Level and course progress -------------------------------------- */}
      <Card className="mt-6 p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <p className="text-sm text-slate-500 dark:text-slate-400">PostgreSQL progress</p>
            <p className="mt-0.5 text-lg font-semibold text-slate-900 dark:text-slate-100">
              Level {data.level.level} · {data.level.title}
            </p>
          </div>
          <p className="text-sm text-slate-500 tabular-nums dark:text-slate-400">
            {data.level.totalXp.toLocaleString()} XP
            {data.level.xpForNextLevel !== null && (
              <>
                {' '}
                · {(data.level.xpForNextLevel - data.level.xpIntoLevel).toLocaleString()} to next
                level
              </>
            )}
          </p>
        </div>

        <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
          <div
            className="h-full rounded-full bg-brand-600 transition-[width] duration-500"
            style={{ width: `${Math.round(data.level.ratio * 100)}%` }}
          />
        </div>

        <div className="mt-5 grid grid-cols-2 gap-5 border-t border-slate-200 pt-4 sm:grid-cols-4 dark:border-slate-800">
          <Stat
            label="Exercises solved"
            value={data.progress.exercisesSolved.toLocaleString()}
            sub={`of ${data.progress.exercisesTotal}`}
            icon={CheckCircle2}
          />
          <Stat
            label="Lessons"
            value={data.progress.lessonsCompleted.toLocaleString()}
            sub={`of ${data.progress.lessonsTotal}`}
            icon={Target}
          />
          <Stat
            label="Accuracy"
            value={data.accuracy.percent === null ? '—' : `${data.accuracy.percent}%`}
            sub={
              data.accuracy.attempts === 0
                ? 'no attempts yet'
                : `${data.accuracy.correct}/${data.accuracy.attempts} submissions`
            }
            icon={Activity}
          />
          <Stat
            label="Streak"
            value={`${data.streak.current}`}
            sub={data.streak.current === 1 ? 'day' : 'days'}
            icon={Flame}
          />
        </div>
      </Card>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* --- Continue learning ------------------------------------------ */}
          <Card>
            <CardHeader title="Continue learning" />
            <div className="p-5">
              {data.nextLesson ? (
                <Link
                  to={`/learn/${data.nextLesson.slug}`}
                  className="group flex items-center gap-3 rounded-lg bg-slate-50 p-4 hover:bg-slate-100 dark:bg-slate-800/50 dark:hover:bg-slate-800"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-medium tracking-wide text-slate-400 uppercase dark:text-slate-400">
                      Level {data.nextLesson.level} · {data.nextLesson.moduleTitle}
                    </p>
                    <p className="mt-0.5 font-medium text-slate-900 dark:text-slate-100">
                      {data.nextLesson.title}
                    </p>
                  </div>
                  <ArrowRight
                    className="size-4 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5"
                    aria-hidden="true"
                  />
                </Link>
              ) : (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  You have completed every lesson. Try the playground or interview mode.
                </p>
              )}
            </div>
          </Card>

          {/* --- Recent activity -------------------------------------------- */}
          <Card>
            <CardHeader title="Recent activity" />
            {data.recentActivity.length === 0 ? (
              <p className="px-5 py-6 text-sm text-slate-500 dark:text-slate-400">
                Submit an answer and it will appear here.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {data.recentActivity.map((entry, index) => (
                  <li key={`${entry.at}-${index}`} className="flex items-center gap-3 px-5 py-2.5">
                    {entry.correct ? (
                      <CheckCircle2
                        className="size-4 shrink-0 text-green-500"
                        aria-label="correct"
                      />
                    ) : (
                      <XCircle
                        className="size-4 shrink-0 text-slate-300 dark:text-slate-500"
                        aria-label="incorrect"
                      />
                    )}
                    <span className="min-w-0 flex-1 truncate text-sm text-slate-700 dark:text-slate-300">
                      {entry.lessonSlug ? (
                        <Link to={`/learn/${entry.lessonSlug}`} className="hover:underline">
                          {entry.exerciseTitle}
                        </Link>
                      ) : (
                        entry.exerciseTitle
                      )}
                    </span>
                    <span className="shrink-0 text-[11px] text-slate-400 capitalize dark:text-slate-400">
                      {entry.difficulty}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          {/* --- Weak areas -------------------------------------------------- */}
          <Card>
            <CardHeader title="Weak areas" description="Topics with your lowest accuracy" />
            <div className="p-5">
              {data.weakTopics.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Solve a few more exercises and your weakest topics will show up here.
                </p>
              ) : (
                <ul className="space-y-2.5">
                  {data.weakTopics.map((topic) => (
                    <li key={topic.tag}>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="flex items-center gap-1.5 text-sm text-slate-700 dark:text-slate-300">
                          <TrendingDown className="size-3.5 text-amber-500" aria-hidden="true" />
                          {topic.tag.replace(/-/g, ' ')}
                        </span>
                        <span className="text-xs text-slate-500 tabular-nums dark:text-slate-400">
                          {topic.accuracy}%
                        </span>
                      </div>
                      <div className="mt-1 h-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                        <div
                          className="h-full rounded-full bg-amber-500"
                          style={{ width: `${topic.accuracy}%` }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>

          {/* --- Activity ---------------------------------------------------- */}
          <Card>
            <CardHeader title="Last 30 days" />
            <div className="p-5">
              <ActivityStrip days={data.activityDays} />
              <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                Longest streak: {data.streak.longest} day{data.streak.longest === 1 ? '' : 's'}
              </p>
            </div>
          </Card>

          {/* --- Badges ------------------------------------------------------ */}
          <Card>
            <CardHeader
              title="Badges"
              description={`${earnedBadges.length} of ${data.badges.length} earned`}
            />
            <ul className="grid grid-cols-1 gap-1 p-3">
              {data.badges.map((badge) => (
                <li
                  key={badge.slug}
                  className={cn(
                    'flex items-start gap-2.5 rounded-lg p-2',
                    badge.earned ? 'bg-amber-50 dark:bg-amber-950/25' : 'opacity-50',
                  )}
                >
                  <Award
                    className={cn(
                      'mt-0.5 size-4 shrink-0',
                      badge.earned ? 'text-amber-500' : 'text-slate-300 dark:text-slate-500',
                    )}
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-slate-900 dark:text-slate-100">
                      {badge.name}
                    </p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      {badge.description}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
